// Подсветка на всей сетке: одна задача, событие или отпуск. Полоса по датам через все строки,
// задачи вне этих дат гаснут — видно, с чем пересекается. Личная настройка `highlight`: для показа
// на созвоне с расшаренным экраном; остальным не видна.
import { useCallback, useMemo, type RefObject } from 'react';
import { useSetting, useTable } from '@core/store';
import { Highlighter } from 'lucide-react';
import { Button } from '@core/ui/Button';
import { overlaps, type ISODate } from '@core/dates';
import { barGeometry, type Grid } from './geometry';
import { vacationLabel } from './data';
import { useEscape } from './interaction';

const JUMP_TRIES = 30;      // кадров ждать бар после показа скрытой строки
const JUMP_FOCUS_MS = 80;   // меню (Radix) при закрытии возвращает фокус на пилюлю — фокус бару после этого
const JUMP_LEFT_RATIO = 1 / 3; // левый край бара — на треть ширины тела от его левого края

export type Spot = { kind: 'task' | 'event' | 'vacation'; id: string };
export type SpotRange = { kind: Spot['kind']; id: string; start_date: ISODate; end_date: ISODate; label: string };

/** Как рисовать бар при подсветке: сам подсвеченный — кольцо, вне дат — гаснет. */
export const spotProps = (t: { id: string; start_date: ISODate; end_date: ISODate }, spot: SpotRange | null): { spotlit: boolean; dim: boolean } =>
  ({ spotlit: spot?.kind === 'task' && spot.id === t.id, dim: !!spot && !overlaps(t.start_date, t.end_date, spot.start_date, spot.end_date) });

/** Полоса подсветки в пикселях сетки или null, если диапазон вне неё. */
export const spotBox = (range: { start_date: ISODate; end_date: ISODate } | null, grid: Grid): { left: number; width: number } | null => {
  if (!range) return null;
  const g = barGeometry(range, grid);
  return g ? { left: g.left, width: g.width } : null;
};

type SpotTables = {
  tasks: Map<string, { start_date: ISODate; end_date: ISODate; title: string; in_backlog: boolean }>;
  events: Map<string, { start_date: ISODate; end_date: ISODate; name: string }>;
  vacations: Map<string, { start_date: ISODate; end_date: ISODate; note: string | null; person_id: string }>;
  people: Map<string, { name: string }>;
};

/** Даты и подпись подсвеченного из живых строк; нет строки или задача в бэклоге — null. */
export function spotRangeOf(spot: Spot | null, { tasks, events, vacations, people }: SpotTables): SpotRange | null {
  if (!spot) return null;
  if (spot.kind === 'task') { const t = tasks.get(spot.id); return t && !t.in_backlog ? { ...spot, start_date: t.start_date, end_date: t.end_date, label: t.title } : null; }
  if (spot.kind === 'event') { const e = events.get(spot.id); return e ? { ...spot, start_date: e.start_date, end_date: e.end_date, label: e.name } : null; }
  const v = vacations.get(spot.id);
  return v ? { ...spot, start_date: v.start_date, end_date: v.end_date, label: vacationLabel(v, people.get(v.person_id)) } : null;
}

export type Spotlight = {
  range: SpotRange | null;
  is: (kind: Spot['kind'], id: string) => boolean;
  toggle: (spot: Spot) => void;
  clear: () => void;
};

const HIGHLIGHT_KEY = 'highlight';
/** Значение строки highlight → подсветка; мусор и пусто — null. */
export const spotOf = (value: unknown): Spot | null => {
  const v = value as { kind?: unknown; id?: unknown } | null;
  return v && (v.kind === 'task' || v.kind === 'event' || v.kind === 'vacation') && typeof v.id === 'string' ? { kind: v.kind, id: v.id } : null;
};

export function useSpotlight(): Spotlight {
  const tasks = useTable('task');
  const events = useTable('event');
  const vacations = useTable('vacation');
  const people = useTable('person');
  const [value, setValue] = useSetting<unknown>(HIGHLIGHT_KEY, null);
  const spot = useMemo(() => spotOf(value), [value]);
  const range = useMemo(() => spotRangeOf(spot, { tasks, events, vacations, people }), [spot, tasks, events, vacations, people]);
  const toggle = useCallback((s: Spot) => setValue(spot && spot.kind === s.kind && spot.id === s.id ? null : s), [setValue, spot]);
  const clear = useCallback(() => setValue(null), [setValue]);
  useEscape(!!range, clear);
  return useMemo(() => ({ range, is: (kind, id) => !!spot && spot.kind === kind && spot.id === id, toggle, clear }), [range, spot, toggle, clear]);
}

// прыжок с другого экрана (история → Projects): id ждёт, пока экран смонтируется и заберёт его
let pendingJump: string | null = null;
export const requestJump = (taskId: string) => { pendingJump = taskId; };
export const takeJump = (): string | null => { const id = pendingJump; pendingJump = null; return id; };

/** Прокрутить тело сетки к бару и дать ему фокус; reveal — показать скрытую строку, бар ждём несколько кадров. */
export function jumpToTask(gridRef: RefObject<HTMLDivElement | null>, taskId: string, reveal?: () => void) {
  reveal?.();
  let tries = 0;
  const attempt = () => {
    const g = gridRef.current;
    const el = g?.querySelector<HTMLElement>(`[data-task="${taskId}"]`);
    if (!g || !el) { if (tries++ < JUMP_TRIES) requestAnimationFrame(attempt); return; }
    const gb = g.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    // строка бара — в середину по вертикали; по горизонтали двигаем, только если бар не виден целиком
    const fits = r.left >= gb.left && r.right <= gb.right;
    const top = g.scrollTop + (r.top - gb.top) - (gb.height - r.height) / 2;
    const left = fits ? g.scrollLeft : g.scrollLeft + (r.left - gb.left) - gb.width * JUMP_LEFT_RATIO;
    g.scrollTo({ top, left, behavior: 'smooth' });
    setTimeout(() => el.focus({ preventScroll: true }), JUMP_FOCUS_MS);
  };
  // сетка только что смонтирована (пришли с другого экрана) — плавный scrollTo до первого кадра браузер молча
  // отбрасывает; ждём два кадра
  requestAnimationFrame(() => requestAnimationFrame(attempt));
}

/** Чип в тулбаре: что подсвечено, клик по названию — прыгнуть к задаче (если она в сетке), × — снять. */
export function SpotlightChip({ spotlight, grid, onJump }: { spotlight: Spotlight; grid: Grid; onJump: (taskId: string) => void }) {
  const r = spotlight.range;
  if (!r) return null;
  const canJump = r.kind === 'task' && spotBox(r, grid) !== null;
  return (
    <span className="flex items-center gap-1 text-sm rounded-md bg-amber-100 border border-amber-300 pl-2 pr-1 h-8 max-w-52 min-w-0 shrink" data-spotlight={r.id} title={`Highlighted (only for you): ${r.label}`}>
      <Highlighter className="w-4 h-4 text-amber-700 shrink-0" aria-label="Highlighted" />
      <button type="button" className={`min-h-0 truncate font-medium text-amber-950 ${canJump ? 'hover:underline' : 'cursor-default'}`} title={canJump ? 'Jump to the task' : r.label} onClick={() => canJump && onJump(r.id)}>{r.label}</button>
      <Button variant="ghost" size="sm" className="px-1.5 h-6 text-amber-900" onClick={spotlight.clear} title="Remove the highlight (Esc)" aria-label="Remove the highlight">×</Button>
    </span>
  );
}
